import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import * as Sentry from '@sentry/angular-ivy';
import { AppleHealthUpsellComponent } from '../modals/apple-health-upsell/apple-health-upsell.component';
import { AppleHealthService } from './apple-health.service';
import { GlobalEventsService } from './event.service';
import { UserService } from './user.service';

const PROMPT_DELAY_MS = 1800;

export const APPLE_HEALTH_UPSELL_FINISHED = 'apple-health-upsell-finished';

@Injectable({
  providedIn: 'root'
})
export class AppleHealthPromptService {

  private promptScheduled = false;

  constructor(
    private appleHealthService: AppleHealthService,
    private eventService: GlobalEventsService,
    private modalCtrl: ModalController,
    private userService: UserService,
  ) {}

  /** Show the upsell once after auth on iOS/Android, if the user has not connected or dismissed. */
  async maybeShowUpsellAfterAuth(): Promise<void> {
    if (!this.appleHealthService.isNativeHealthPlatform() || this.promptScheduled) {
      return;
    }

    const user = this.userService.getAppUser();
    if (!user?.uid) {
      return;
    }

    try {
      const shouldShow = await this.appleHealthService.shouldPrompt(user.uid);
      if (!shouldShow) {
        return;
      }
    } catch (error) {
      Sentry.captureException(error?.originalError || error, {
        tags: {
          feature: this.appleHealthService.isNativeAndroid() ? 'google-health' : 'apple-health',
          step: 'should-prompt',
        },
      });
      return;
    }

    this.promptScheduled = true;
    setTimeout(async () => {
      try {
        const stillShouldShow = await this.appleHealthService.shouldPrompt(user.uid);
        if (!stillShouldShow) {
          return;
        }
        const top = await this.modalCtrl.getTop();
        if (top) {
          return;
        }
        await this.presentUpsell(user.uid);
      } catch (error) {
        Sentry.captureException(error?.originalError || error, {
          tags: {
            feature: this.appleHealthService.isNativeAndroid() ? 'google-health' : 'apple-health',
            step: 'present-upsell',
          },
        });
      } finally {
        this.promptScheduled = false;
      }
    }, PROMPT_DELAY_MS);
  }

  async presentUpsell(userId: string): Promise<'connected' | 'dismissed' | 'closed'> {
    const modal = await this.modalCtrl.create({
      component: AppleHealthUpsellComponent,
      componentProps: { userId, mode: 'upsell' },
      cssClass: 'apple-health-upsell-modal',
      // Block backdrop/gesture dismiss; promo X still closes via dismiss role.
      backdropDismiss: false,
      canDismiss: async (_data, role) => role !== 'backdrop' && role !== 'gesture',
    });
    await modal.present();
    const { role } = await modal.onWillDismiss();
    const resolved = (role as 'connected' | 'dismissed' | 'closed') || 'closed';
    this.eventService.publish({
      type: APPLE_HEALTH_UPSELL_FINISHED,
      role: resolved,
      userId,
    });
    return resolved;
  }

  /** Same sync progress screen as the upsell, without the connect promo. */
  async presentSync(userId: string): Promise<'synced' | 'error' | 'closed'> {
    const modal = await this.modalCtrl.create({
      component: AppleHealthUpsellComponent,
      componentProps: { userId, mode: 'sync' },
      cssClass: 'apple-health-upsell-modal',
      backdropDismiss: false,
      canDismiss: async (_data, role) => role !== 'backdrop' && role !== 'gesture',
    });
    await modal.present();
    const { role } = await modal.onWillDismiss();
    return (role as 'synced' | 'error' | 'closed') || 'closed';
  }
}
